import { useRef, useEffect } from 'react';
import './Invoice.css';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { QRCodeCanvas } from 'qrcode.react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const formatNumber = (number, locale) => {
  // Ensure the number is a properly parsed float
  const parsedNumber = parseFloat(number);
  if (isNaN(parsedNumber)) {
    return '0.00';
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(parsedNumber);
};

const formatDate = (dateString, locale) => {
  const date = new Date(dateString);
  return moment(date).format('DD MMM YYYY');
};

// Payment method transformation function
const transformPaymentMethod = (paymentMethod) => {
  const paymentMethods = {
    'bank_transfer': 'Bank Transfer',
    'credit_card': 'Credit Card',
    'cash': 'Cash',
    'debit_card': 'Debit Card',
    'online_payment': 'Online Payment',
    'cheque': 'Cheque',
    'mobile_payment': 'Mobile Payment'
  };
  
  return paymentMethods[paymentMethod] || paymentMethod || 'N/A';
};

// Generate QR code data from receipt number
const generateQRCodeData = (receiptNo) => {
 if (!receiptNo) return 'RECEIPT-000000';
 return receiptNo;
};

const ReceiptModal = ({ show, onClose, registration }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const branchInfo = {
  1: {
    name: "Keaes Academics (Ekamai)",
    address: [
      "982/22 Gateway Ekamai, 4th Fl. Sukhumvit Rd,",
      "Phra Khanong, Khlong Toei, Bangkok 10110"
    ],
    taxId: "0000000000000",
    tel: "02-108-2922",
    email: "contact@keaes.com"
  },
  2: {
    name: "Keaes Academics (Ramkhamhaeng)",
    address: [
      "61 Marketplace, 3rd Fl., Ramkhamhaeng Rd,",
      "Saphan Sung, Bangkok 10240"
    ],
    taxId: "0000000000000",
    tel: "02-729-5826",
    email: "contact@keaes.com"
  }
};

 const branch = branchInfo[registration.branch_id] || branchInfo[1];

  // This useEffect will help show a warning if inventory data is missing
  useEffect(() => {
    if (registration && !registration.inventory_items) {
    }
  }, [registration]);

  if (!show || !registration) return null;


  
  let classItems = [];
  
  // Handle the class_items data
  if (registration.class_items) {
    if (Array.isArray(registration.class_items)) {
      classItems = registration.class_items;

    } else if (typeof registration.class_items === 'string') {
      try {
        classItems = JSON.parse(registration.class_items);

      } catch (e) {
        console.error('Error parsing class_items string:', e);
        classItems = [];
      }
    } else {

      classItems = [];
    }
  } else {

  }
  
  
  // Fallback: if no class_items but we have class_code, create a single item
  if (classItems.length === 0 && registration.class_code) {
    classItems = [{
      class_code: registration.class_code,
      price: registration.final_price || registration.pay_amount || 0
    }];
  }
  
  const isMultipleClasses = classItems.length > 1;

  
  // Calculate class total from class_items array
  const classTotalPrice = classItems.reduce((sum, item) => {
  return sum + (parseFloat(item.price) || 0);
  }, 0);

    const totalBeforeDiscount = classTotalPrice;
    const discountAmount = parseFloat(registration.discount || 0);
    const totalAfterDiscount = totalBeforeDiscount - discountAmount;
    const vatAmount = parseFloat(registration.vat || 0);
    const grandTotal = parseFloat(registration.final_price || 0) || (totalAfterDiscount + vatAmount);
    const payAmount = parseFloat(registration.pay_amount || 0);
    const remainingBalance = parseFloat(registration.remaining_balance || 0);


  // Generate QR code data
  const qrCodeData = generateQRCodeData(registration.receipt_no);

  const imgToDataURL = (imgEl) => {
    try {
      if (!imgEl || !imgEl.complete || !imgEl.naturalWidth) return null;
      const canvas = document.createElement('canvas');
      canvas.width = imgEl.naturalWidth;
      canvas.height = imgEl.naturalHeight;
      canvas.getContext('2d').drawImage(imgEl, 0, 0);
      return canvas.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  };

  const handleDownloadPDF = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let y = 15;

    // Watermark if refunded
    if (registration.confirmation_status === 'refund') {
      pdf.setTextColor(255, 200, 200);
      pdf.setFontSize(72);
      pdf.setFont('helvetica', 'bold');
      pdf.text('REFUND', pageW / 2, pageH / 2, { align: 'center', angle: 45 });
      pdf.setTextColor(51, 51, 51);
    }

    // Logo (top-left)
    const logoEl = componentRef.current?.querySelector('img[src*="keaes"]');
    const logoData = imgToDataURL(logoEl);
    const logoSize = 22;
    const textLeft = logoData ? margin + logoSize + 4 : margin;
    if (logoData) {
      pdf.addImage(logoData, 'PNG', margin, y, logoSize, logoSize);
    }

    // Branch info (to right of logo)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(51, 51, 51);
    pdf.text(branch.name, textLeft, y);
    y += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(74, 74, 74);
    branch.address.forEach((line) => { pdf.text(line, textLeft, y); y += 4; });
    pdf.text(`TAX ID ${branch.taxId}`, textLeft, y); y += 4;
    pdf.text(`Tel: ${branch.tel}`, textLeft, y); y += 4;
    pdf.text(`Email: ${branch.email}`, textLeft, y); y += 4;
    y = Math.max(y, 15 + logoSize);

    // Receipt box (right)
    const boxW = 65;
    const boxH = 22;
    const boxX = pageW - margin - boxW;
    const boxY = 15;
    pdf.setDrawColor(180);
    pdf.setFillColor(230, 230, 230);
    pdf.rect(boxX, boxY, boxW, boxH, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(51, 51, 51);
    pdf.text('RECEIPT', boxX + boxW / 2, boxY + 6, { align: 'center' });
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`No.: ${registration.receipt_no || '-'}`, boxX + 3, boxY + 13);
    pdf.text(`Date: ${formatDate(registration.timestamp || registration.date, i18n.language)}`, boxX + 3, boxY + 18);

    y = Math.max(y, boxY + boxH) + 5;

    // Customer info
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(51, 51, 51);
    pdf.text('Customer Information', margin, y);
    y += 4;
    const custBoxH = 18;
    pdf.setDrawColor(200);
    pdf.setFillColor(249, 249, 249);
    pdf.rect(margin, y, pageW - 2 * margin, custBoxH, 'FD');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    const customerName = `${registration.firstname || ''} ${registration.lastname || registration.student_name || ''}`.trim() || 'N/A';
    pdf.text(`Name: ${customerName}`, margin + 3, y + 5);
    pdf.text(`Address: ${registration.address || 'N/A'}`, margin + 3, y + 10);
    pdf.text(`Phone: ${registration.phone || 'N/A'}`, margin + 3, y + 15);
    y += custBoxH + 5;

    // Items table
    const items = classItems.length > 0 ? classItems : [{
      class_code: registration.class_code || registration.class_codes_display || 'Course',
      price: registration.final_price || registration.pay_amount || 0,
    }];
    const body = items.map((item, i) => [
      String(i + 1),
      item.class_code || 'Class',
      '1',
      'Course',
      `THB ${formatNumber(item.price, i18n.language)}`,
      `THB ${formatNumber(item.price, i18n.language)}`,
    ]);
    autoTable(pdf, {
      startY: y,
      head: [['#', 'Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 2, textColor: [51, 51, 51] },
      headStyles: { fillColor: [200, 200, 200], textColor: [51, 51, 51], fontStyle: 'bold' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 'auto' },
        2: { halign: 'center', cellWidth: 15 },
        3: { halign: 'center', cellWidth: 20 },
        4: { halign: 'right', cellWidth: 32 },
        5: { halign: 'right', cellWidth: 32, fontStyle: 'bold' },
      },
    });
    y = pdf.lastAutoTable.finalY + 5;

    // Summary box (right aligned)
    const sumRows = [
      ['Subtotal', `THB ${formatNumber(totalBeforeDiscount, i18n.language)}`, false],
    ];
    if (discountAmount > 0) sumRows.push(['Discount', `- THB ${formatNumber(discountAmount, i18n.language)}`, false]);
    sumRows.push(['Total After Discount', `THB ${formatNumber(totalAfterDiscount, i18n.language)}`, false]);
    if (vatAmount > 0) sumRows.push(['VAT 7%', `THB ${formatNumber(vatAmount, i18n.language)}`, false]);
    sumRows.push(['Grand Total', `THB ${formatNumber(grandTotal, i18n.language)}`, true]);
    sumRows.push(['Paid Amount', `THB ${formatNumber(payAmount, i18n.language)}`, true]);
    if (remainingBalance > 0) sumRows.push(['Remaining Balance', `(THB ${formatNumber(remainingBalance, i18n.language)})`, false]);

    const sumBoxW = 90;
    const sumBoxX = pageW - margin - sumBoxW;
    const lineH = 5;
    const sumBoxH = sumRows.length * lineH + 4;
    pdf.setDrawColor(108, 117, 125);
    pdf.setFillColor(249, 249, 249);
    pdf.rect(sumBoxX, y, sumBoxW, sumBoxH, 'FD');
    pdf.setFontSize(9);
    let sy = y + 5;
    sumRows.forEach(([label, value, bold]) => {
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setTextColor(51, 51, 51);
      pdf.text(`${label}:`, sumBoxX + 3, sy);
      pdf.text(String(value), sumBoxX + sumBoxW - 3, sy, { align: 'right' });
      sy += lineH;
    });
    y += sumBoxH + 6;

    // Payment & Transaction
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(51, 51, 51);
    pdf.text('Payment & Transaction Information', pageW / 2, y, { align: 'center' });
    y += 3;
    const payLines = [`Method: ${transformPaymentMethod(registration.payment_method)}`];
    if (registration.payment_method === 'bank_transfer' && (registration.bank_name || registration.account_number)) {
      let bankLine = `Bank: ${registration.bank_name || ''} | Account: ${registration.account_number || ''}`;
      if (registration.account_name) bankLine += ` | Name: ${registration.account_name}`;
      payLines.push(bankLine);
    }
    if (registration.invoice_no) payLines.push(`Invoice No: ${registration.invoice_no}`);
    if (registration.memo) payLines.push(`Memo: ${registration.memo}`);

    const payBoxW = 160;
    const payBoxX = (pageW - payBoxW) / 2;
    const payBoxH = payLines.length * 5 + 6;
    pdf.setDrawColor(200);
    pdf.setFillColor(249, 249, 249);
    pdf.rect(payBoxX, y, payBoxW, payBoxH, 'FD');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    let py = y + 5;
    payLines.forEach((line) => {
      pdf.text(line, payBoxX + 3, py);
      py += 5;
    });
    y += payBoxH + 8;

    // Footer row: QR (left), signature (center), print info (right)
    const footerY = y;
    let qrDataURL = null;
    try {
      const qrCanvas = componentRef.current?.querySelector('canvas');
      if (qrCanvas) qrDataURL = qrCanvas.toDataURL('image/png');
    } catch (e) { /* ignore */ }

    if (qrDataURL) {
      pdf.addImage(qrDataURL, 'PNG', margin, footerY, 20, 20);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RECEIPT', margin + 10, footerY + 23, { align: 'center' });
    }

    const sigCenterX = pageW / 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Authorized by', sigCenterX, footerY + 4, { align: 'center' });
    pdf.setDrawColor(108, 117, 125);
    pdf.line(sigCenterX - 30, footerY + 18, sigCenterX + 30, footerY + 18);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    const authName = registration.created_by_firstname && registration.created_by_lastname
      ? `${registration.created_by_firstname} ${registration.created_by_lastname}`
      : 'Authorized Staff';
    pdf.text(authName, sigCenterX, footerY + 22, { align: 'center' });

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('Printed On:', pageW - margin, footerY + 4, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    pdf.text(moment().format('DD MMM YYYY'), pageW - margin, footerY + 9, { align: 'right' });
    pdf.text(moment().format('HH:mm:ss'), pageW - margin, footerY + 13, { align: 'right' });

    y = footerY + 28;

    // Note
    pdf.setFontSize(8);
    pdf.setTextColor(108, 117, 125);
    const note = 'Note: We appreciate your business. Please note that all transactions are final, and refunds cannot be processed.';
    const noteLines = pdf.splitTextToSize(note, pageW - 2 * margin);
    pdf.text(noteLines, pageW / 2, y, { align: 'center' });
    y += noteLines.length * 4;

    if (isMultipleClasses) {
      pdf.text(`This receipt covers payment for ${classItems.length} classes in a single transaction.`, pageW / 2, y, { align: 'center' });
    }

    pdf.save(`receipt_${registration.receipt_no || 'receipt'}.pdf`);
  };

  return (
    <div className="invoice-modal-overlay" style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="invoice-modal" ref={componentRef} style={{ 
        backgroundColor: 'white',
        padding: '16px',
        width: '210mm',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative',
      }}>
        {/* Refund watermark if applicable */}
        {registration.confirmation_status === 'refund' && (
          <div className="refund-watermark" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: '72px',
            color: 'rgba(255, 0, 0, 0.15)',
            fontWeight: 'bold',
            zIndex: 0,
            pointerEvents: 'none'
          }}>
            {t('refund')}
          </div>
        )}
        
        <span className="modal-close-button" onClick={onClose} style={{
          position: 'absolute',
          top: '8px',
          right: '12px',
          fontSize: '20px',
          cursor: 'pointer',
          zIndex: 1,
          color: '#333',
          fontWeight: 'bold'
        }}>&times;</span>
        
        {/* Header section with logo and company info - Compact */}
        <div className="modal-header" style={{ 
          display: 'flex', 
          marginBottom: '12px',
          alignItems: 'flex-start'
        }}>
          {/* Left side - Logo */}
          <div className="company-logo" style={{ 
            width: '70px', 
            height: '70px',
            marginRight: '16px'
          }}>
            <img src="/images/keaes150.png" alt="KEAES" style={{ 
              width: '120%',
              height: 'auto'
            }} />
          </div>
          
          {/* Middle - Company info */}

           {/* Middle - Company info */}
            <div style={{ flex: 1, paddingTop: '2px' }}>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: 'bold', 
                marginBottom: '4px',
                marginTop: 0,
                color: '#333',
                textAlign: 'left',
                marginLeft: '24px'
              }}>
                {branch.name}
              </h3>
              {branch.address.map((line, idx) => (
                <p key={idx} style={{ 
                  fontSize: '10px', 
                  margin: '1px 0',
                  lineHeight: '1.2',
                  color: '#4a4a4a',
                  textAlign: 'left',
                  marginLeft: '24px'
                }}>
                  {line}
                </p>
              ))}
              <p style={{ fontSize: '10px', margin: '1px 0', lineHeight: '1.2', color: '#4a4a4a', textAlign: 'left', marginLeft: '24px' }}>
                TAX ID {branch.taxId}
              </p>
              <p style={{ fontSize: '10px', margin: '1px 0', lineHeight: '1.2', color: '#4a4a4a', textAlign: 'left', marginLeft: '24px' }}>
                Tel: {branch.tel}
              </p>
              <p style={{ fontSize: '10px', margin: '1px 0', lineHeight: '1.2', color: '#4a4a4a', textAlign: 'left', marginLeft: '24px' }}>
                Email: {branch.email}
              </p>
            </div>


          
          {/* Right side - Receipt box */}
         <div style={{ 
  background: 'rgba(200, 200, 200, 0.3)', 
  padding: '12px', 
  color: '#333',
  borderRadius: '6px',
  width: '180px',
  textAlign: 'center',
  marginLeft: 'auto',
  maxWidth: 180
}}>
  <h2 style={{ 
    color: '#333', 
    fontSize: '16px', 
    margin: '0 0 4px 0',
    fontWeight: 'bold',
    textAlign: 'center'
  }}>
    {t('receipt').toUpperCase()}
  </h2>
  <div className="invoice-details" style={{ marginBottom: 4 }}>
    <div className="detail-row" style={{ marginBottom: 2 }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: '10px', 
        margin: '2px 0' 
      }}>
        <span style={{ fontWeight: 'bold', color: '#333' }}>{t('No.')}:</span>
        <span style={{ color: '#333', marginLeft: 6 }}>{registration.receipt_no || '-'}</span>
      </div>
    </div>
    <div className="detail-row" style={{ marginBottom: 2 }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: '10px', 
        margin: '2px 0' 
      }}>
        <span style={{ fontWeight: 'bold', color: '#333' }}>{t('date')}:</span>
        <span style={{ color: '#333', marginLeft: 6 }}>{formatDate(registration.timestamp || registration.date, i18n.language)}</span>
      </div>
    </div>
  </div>
</div>
        </div>
        
        
        {/* Customer Information - Compact */}
        <div className="customer-info" style={{ marginBottom: '12px' }}>
          <h3 style={{ 
            fontSize: '13px', 
            fontWeight: 'bold', 
            marginBottom: '8px',
            color: '#333',
          }}>{t('Customer Information')}</h3>
          <div style={{
            backgroundColor: '#f9f9f9',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '8px 12px'
          }}>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('name')}:</strong> {registration.firstname} {registration.lastname || registration.student_name || t('na')}
            </p>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('address')}:</strong> {registration.address || t('na')}
            </p>
            <p style={{ fontSize: '11px', margin: '3px 0', color: '#333' }}>
              <strong>{t('phone')}:</strong> {registration.phone || t('na')}
            </p>
          </div>
        </div>
        
        <hr style={{ 
          border: 'none', 
          height: '1px', 
          backgroundColor: '#ccc', 
          margin: '12px 0'
        }} />
        
        
        <div className="invoice-table" style={{ marginTop: '12px' }}>
          <div style={{ 
            width: '100%', 
            fontSize: '11px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            {/* Header Row */}
            <div style={{
              background: 'rgba(200, 200, 200, 0.3)', 
              padding: '4px 8px',
              borderBottom: '1px solid #495057',
              fontSize: 11,
              fontWeight: 'bold',
              color: '#333',
              borderRadius: '4px 4px 0 0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '5%', textAlign: 'center' }}>#</div>
                <div style={{ width: '45%', paddingLeft: '4px' }}>{t('description')}</div>
                <div style={{ width: '10%', textAlign: 'center' }}>{t('quantity')}</div>
                <div style={{ width: '10%', textAlign: 'center' }}>{t('unit')}</div>
                <div style={{ width: '15%', textAlign: 'right' }}>{t('unitPrice')}</div>
                <div style={{ width: '15%', textAlign: 'right' }}>{t('total')}</div>
              </div>
            </div>

            {/* Table Body as Card Rows */}
            <div>
              {/* Render all class items */}
              {classItems && classItems.length > 0 ? (
                classItems.map((classItem, index) => (
                  <div 
                    key={`class-${index}`} 
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                      padding: '4px 8px',
                      borderBottom: index === classItems.length - 1 ? 'none' : '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '28px'
                    }}
                  >
                    <div style={{ width: '5%', textAlign: 'center', color: '#333', fontWeight: 'bold', fontSize: '11px' }}>{index + 1}</div>
                    <div style={{ width: '45%', paddingLeft: '4px', color: '#333' }}>
                      <div style={{ fontWeight: '500', fontSize: '11px' }}>{classItem.class_code || 'Class'}</div>
                      {isMultipleClasses && (
                        <div style={{ fontSize: '9px', color: '#495057' }}>
                          (Class {index + 1} of {classItems.length})
                        </div>
                      )}
                    </div>
                    <div style={{ width: '10%', textAlign: 'center', color: '#333', fontSize: '11px' }}>1</div>
                    <div style={{ width: '10%', textAlign: 'center', color: '#333', fontSize: '11px' }}>Course</div>
                    <div style={{ width: '15%', textAlign: 'right', color: '#333', fontSize: '11px' }}>฿{formatNumber(classItem.price, i18n.language)}</div>
                    <div style={{ width: '15%', textAlign: 'right', color: '#333', fontWeight: 'bold', fontSize: '11px' }}>฿{formatNumber(classItem.price, i18n.language)}</div>
                  </div>
                ))
              ) : (
                // Fallback for old single class structure or missing class_items
                <div style={{ 
                  backgroundColor: '#ffffff',
                  padding: '4px 8px',
                  borderBottom: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '28px'
                }}>
                  <div style={{ width: '5%', textAlign: 'center', color: '#333', fontWeight: 'bold', fontSize: '11px' }}>1</div>
                  <div style={{ width: '45%', paddingLeft: '4px', color: '#333', fontWeight: '500', fontSize: '11px' }}>
                    {registration.class_code || registration.class_codes_display || 'Course'}
                  </div>
                  <div style={{ width: '10%', textAlign: 'center', color: '#333', fontSize: '11px' }}>1</div>
                  <div style={{ width: '10%', textAlign: 'center', color: '#333', fontSize: '11px' }}>Course</div>
                  <div style={{ width: '15%', textAlign: 'right', color: '#333', fontSize: '11px' }}>
                    ฿{formatNumber(registration.final_price || registration.pay_amount || 0, i18n.language)}
                  </div>
                  <div style={{ width: '15%', textAlign: 'right', color: '#333', fontWeight: 'bold', fontSize: '11px' }}>
                    ฿{formatNumber(registration.final_price || registration.pay_amount || 0, i18n.language)}
                  </div>
                </div>
              )}
              
            </div>
          </div>
        </div>
        {/* Summary Information - Compact */}
        <div className="invoice-summary" style={{ 
          fontSize: 11,
          border: '1px solid #6c757d',
          padding: 8,
          background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%)',
          borderRadius: 6,
          width: 280,
          marginLeft: 'auto',
          marginTop: '12px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#333' }}>
            <span>{t('subtotal')}:</span>
            <span>฿{formatNumber(totalBeforeDiscount, i18n.language)}</span>
          </div>
          
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
              <span style={{ color: '#333' }}>{t('discount')}:</span>
              <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>-฿{formatNumber(discountAmount, i18n.language)}</span>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#333' }}>
            <span>{t('Total After Discount')}:</span>
            <span>฿{formatNumber(totalAfterDiscount, i18n.language)}</span>
          </div>
          
          {vatAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#333' }}>
              <span>{t('vat', { percentage: 7 })}:</span>
              <span>฿{formatNumber(vatAmount, i18n.language)}</span>
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '6px 0 4px 0',
            fontWeight: 'bold',
            fontSize: '12px',
            borderTop: '1px solid #6c757d',
            paddingTop: '4px',
            color: '#333'
          }}>
            <span>{t('grandTotal')}:</span>
            <span>฿{formatNumber(grandTotal, i18n.language)}</span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            margin: '4px -8px 0 -8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px',
            backgroundColor: '#6c757d',
            padding: '6px 8px',
            borderRadius: '4px',
            textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
          }}>
            <span>{t('Paid Amount')}:</span>
            <span>฿{formatNumber(payAmount, i18n.language)}</span>
          </div>
          
          {/* Only show remaining balance if needed */}
          {remainingBalance > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              margin: '4px 0 0 0',
              color: '#d32f2f',
              fontWeight: 'bold',
              fontSize: '11px',
              backgroundColor: '#ffebee',
              padding: '4px 6px',
              borderRadius: '4px',
              border: '1px solid #ffcdd2'
            }}>
              <span>{t('Remaining Balance')}:</span>
              <span>(฿{formatNumber(remainingBalance, i18n.language)})</span>
            </div>
          )}
        </div>

        {/* Payment & Transaction Information - Compact */}
        <div className="payment-info" style={{ 
          marginTop: '12px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{ 
            fontSize: '10px',
            textAlign: 'center',
            maxWidth: '400px',
            backgroundColor: '#f9f9f9',
            border: '1px solid #ccc',
            borderRadius: '6px',
            padding: '8px 12px'
          }}>
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '6px', 
              color: '#333', 
              fontSize: '11px',
              borderBottom: '1px solid #ccc',
              paddingBottom: '3px'
            }}>
              {t('Payment & Transaction Information')}
            </div>
            
            <div style={{ lineHeight: '1.4', color: '#333' }}>
              <div><strong>{t('Method')}:</strong> {transformPaymentMethod(registration.payment_method)}</div>
              
              {registration.payment_method === 'bank_transfer' && (registration.bank_name || registration.account_number) && (
                <div style={{ margin: '2px 0' }}>
                  <strong>{t('Bank')}:</strong> {registration.bank_name} | 
                  <strong> {t('Account')}:</strong> {registration.account_number}
                  {registration.account_name && <span> | <strong>{t('Name')}:</strong> {registration.account_name}</span>}
                </div>
              )}
              
              {registration.invoice_no && (
                <div><strong>{t('Invoice No')}:</strong> {registration.invoice_no}</div>
              )}

              {registration.memo && (
                <div style={{ margin: '2px 0' }}>
                  <strong>{t('memo')}:</strong> {registration.memo}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* QR Code, Signature, and Print Info Footer - Compact */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-end',
          marginTop: '16px',
          marginBottom: '12px',
          padding: '12px 0',
          borderTop: '1px solid #ccc'
        }}>
          {/* QR Code Section */}
          <div style={{ 
            width: '25%', 
            textAlign: 'center'
          }}>
            <QRCodeCanvas
              value={qrCodeData}
              size={50}
            />
            <div style={{ 
              fontSize: '9px', 
              marginTop: '4px', 
              color: '#495057',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              fontWeight: 'bold'
            }}>
              RECEIPT
            </div>
          </div>
          
          {/* Signature Section */}
          <div style={{ 
            width: '50%', 
            textAlign: 'center',
            padding: '0 16px'
          }}>
            {registration.signature ? (
              <div>
                <img 
                  src={registration.signature} 
                  alt="Signature" 
                  style={{ 
                    maxWidth: '180px', 
                    maxHeight: '50px', 
                    marginBottom: '4px' 
                  }} 
                />
                <div style={{ 
                  fontSize: '11px',
                  color: '#333',
                  fontWeight: 'bold'
                }}>
                  {t('Authorized Signature')}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ 
                  fontSize: '11px', 
                  fontWeight: 'bold', 
                  marginBottom: '60px',
                  color: '#333'
                }}>
                  Authorized by
                </div>
                <div style={{ 
                  borderTop: '1px solid #6c757d', 
                  width: '240px', 
                  margin: '0 auto',
                  paddingTop: '6px',
                  fontSize: '11px',
                  color: '#333'
                }}>
                  {registration.created_by_firstname && registration.created_by_lastname ? (
                    <div style={{ fontWeight: '500' }}>
                      {registration.created_by_firstname} {registration.created_by_lastname}
                    </div>
                  ) : (
                    <div style={{ color: '#495057' }}>
                      Authorized Staff
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Print Info Section */}
          <div style={{ 
            width: '25%', 
            textAlign: 'right', 
            fontSize: '10px', 
            color: '#495057'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>
              Printed On:
            </div>
            <div style={{ marginBottom: '1px' }}>
              {moment().format('DD MMM YYYY')}
            </div>
            <div>
              {moment().format('HH:mm:ss')}
            </div>
          </div>
        </div>
        
        {/* Note - Compact */}
        <div className="invoice-note" style={{ 
          textAlign: 'center', 
          marginTop: '8px',
          marginBottom: '12px'
        }}>
          <p style={{ fontSize: '9px', color: '#495057', marginBottom: '4px' }}>
            {t('Note: We appreciate your business. Please note that all transactions are final, and refunds cannot be processed.')}
          </p>
          {isMultipleClasses && (
            <p style={{ fontSize: '9px', color: '#495057', marginTop: '2px' }}>
              This receipt covers payment for {classItems.length} classes in a single transaction.
            </p>
          )}
        </div>
        
        {/* Print & Download Buttons - Compact */}
        <div className="invoice-actions"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              marginTop: '12px',
              '@media print': {
                display: 'none'
              }
            }}>
          <button
            onClick={handlePrint}
            style={{
              padding: '8px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: '1px solid #495057',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#495057';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#6c757d';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {t('printReceipt')}
          </button>
          <button
            onClick={handleDownloadPDF}
            style={{
              padding: '8px 20px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: '1px solid #096dd9',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#096dd9';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#1890ff';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {t('Download PDF')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;